import { isPerSqmMetric } from "../components/cubeDataTable/utils/derivedUtils";
import {
  type MetricPreset,
  SAVED_VIEW_VERSION,
  type SavedViewPayload,
  type ViewRows
} from "./savedViewPayload";

/**
 * The views the picker offers before the user saves any of their own, in
 * picker order — widest first. The page still opens on ``Compact YoY``
 * (``DEFAULT_BUILT_IN_VIEW``); list order and the opening view are separate
 * decisions.
 */
export const BUILT_IN_VIEW_KEYS = [
  "Full",
  "OP",
  "Compact YoY",
  "Compact Units"
] as const;

export type BuiltInViewKey = (typeof BUILT_IN_VIEW_KEYS)[number];

const BUDGET_BUILT_IN_VIEW_KEYS: readonly BuiltInViewKey[] =
  BUILT_IN_VIEW_KEYS.filter((key) => key !== "Compact Units");

/** The built-ins ``grid``'s picker offers (see `saved-views-contract.md`). */
export function builtInViewKeysFor(
  grid: "forecast" | "budget"
): readonly BuiltInViewKey[] {
  return grid === "budget" ? BUDGET_BUILT_IN_VIEW_KEYS : BUILT_IN_VIEW_KEYS;
}

/**
 * The One Pager metric list. Code-owned: a release curates it, and a saved
 * view referencing the preset picks up the current list rather than freezing
 * the one that existed when it was saved.
 */
const ONE_PAGER_METRICS = [
  "GS1",
  "GS_DISCOUNT_PERCENTAGE",
  "GS1_AUR",
  "GIS1",
  "GS1_AUC",
  "GS1_COGS",
  "GS2",
  "GS2_AUR",
  "GS2_MARGIN_VALUE",
  "GS2_MARGIN_PERCENTAGE",
  "GS2_PER_SQM_LOCATION_AVG",
  "GR2",
  "GR2_AUR",
  "GIR",
  "RETURN_UNIT_PERCENTAGE",
  "GR2_AUC",
  "COGR",
  "RETURN_COST_PERCENTAGE",
  "NS3",
  "NS3_AUR",
  "NIS3",
  "NS3_AUC",
  "NS3_COGS",
  "NS3_MARGIN_VALUE",
  "NS3_MARGIN_PERCENTAGE",
  "INTAKE_UNITS_TOTAL",
  "INTAKE_UNITS_MAIN",
  "INTAKE_UNITS_REPLENISHMENT",
  "INTAKE_UNITS_INSEASON",
  "INTAKE_UNITS_CLEARANCE",
  "INTAKE_AT_COST_TOTAL",
  "INTAKE_AT_COST_MAIN",
  "INTAKE_AT_COST_REPLENISHMENT",
  "INTAKE_AT_COST_INSEASON",
  "INTAKE_AT_COST_CLEARANCE",
  "INTAKE_AUC_TOTAL",
  "INTAKE_AUC_MAIN",
  "INTAKE_AUC_REPLENISHMENT",
  "INTAKE_AUC_INSEASON",
  "INTAKE_AUC_CLEARANCE",
  "BEGINNING_OF_PERIOD_AT_COST",
  "BEGINNING_OF_PERIOD_AUC",
  "BEGINNING_OF_PERIOD_UNITS",
  "END_OF_PERIOD_AT_COST",
  "END_OF_PERIOD_AUC",
  "END_OF_PERIOD_UNITS",
  "END_OF_PERIOD_UNITS_PER_SQM_LOCATION_AVG",
  "END_OF_PERIOD_AT_COST_PER_SQM_LOCATION_AVG",
  "TARGET_STOCK_AT_COST",
  "TARGET_STOCK_AUC",
  "TARGET_STOCK_UNITS_TOTAL",
  "FORWARD_PERIOD_OF_SALES_AT_COST",
  "FORWARD_PERIOD_OF_SALES",
  "OPEN_TO_BUY_AT_COST",
  "OPEN_TO_BUY_AUC",
  "OPEN_TO_BUY_UNITS",
  "PLANNED_OPEN_TO_BUY_AT_COST",
  "PLANNED_OPEN_TO_BUY_AUC",
  "PLANNED_OPEN_TO_BUY_UNITS",
  "STORE_COUNT",
  "STORE_SQM",
  "GS2_COGS",
  "GS2_AUC",
  "RATE_OF_STOCK_TURNOVER_AT_COST",
  "RATE_OF_STOCK_TURNOVER_UNITS"
];

/** The Compact metric list. Code-owned, same rule as the One Pager's. */
const COMPACT_METRICS = [
  "GS2",
  "GR2",
  "NS3_COGS",
  "INTAKE_AT_COST_TOTAL",
  "END_OF_PERIOD_AT_COST",
  "OPEN_TO_BUY_AT_COST",
  "FORWARD_PERIOD_OF_SALES_AT_COST"
];

/** Store metrics the Per SQM view keeps alongside the per-SQM ones. */
const PER_SQM_EXTRA_METRICS = ["STORE_SQM", "STORE_COUNT"];

/**
 * Row selections per metric catalogue, as the old view tabs narrowed them —
 * without a sort, which is the view's to name. The picker's built-ins
 * (below) and the range-plan modal's own strip start from these.
 */
export const METRIC_PRESET_ROWS: Record<
  MetricPreset,
  Omit<ViewRows, "sortBy">
> = {
  Full: {
    preset: "Full",
    categories: [],
    metrics: [],
    yearOffsets: null
  },
  OP: {
    preset: "OP",
    categories: [],
    metrics: [],
    // The current and the previous fiscal year.
    yearOffsets: [-1, 0]
  },
  Compact: {
    preset: "Compact",
    categories: [],
    metrics: [],
    yearOffsets: null
  },
  "Per SQM": {
    preset: "Per SQM",
    categories: [],
    metrics: [],
    yearOffsets: null
  }
};

/**
 * The views the picker ships with, widest first — each one a metric
 * catalogue plus a sort: ``(Full, None)``, ``(OP, None)``,
 * ``(Compact, YoY)``; and ``Compact Units``, nine hand-picked unit KPIs
 * with no catalogue (decision AA). It leaves the week labels and the
 * planning year to the tenant and the metadata.
 */
export const BUILT_IN_VIEWS: Record<BuiltInViewKey, SavedViewPayload> = {
  Full: {
    version: SAVED_VIEW_VERSION,
    dataShape: { planBy: "ABS" },
    rows: { ...METRIC_PRESET_ROWS.Full, sortBy: "None" }
  },
  OP: {
    version: SAVED_VIEW_VERSION,
    dataShape: { planBy: "IDX" },
    rows: { ...METRIC_PRESET_ROWS.OP, sortBy: "None" }
  },
  "Compact YoY": {
    version: SAVED_VIEW_VERSION,
    dataShape: { planBy: "ABS" },
    rows: { ...METRIC_PRESET_ROWS.Compact, sortBy: "YoY" }
  },
  "Compact Units": {
    version: SAVED_VIEW_VERSION,
    dataShape: {
      calendarView: "Inv 454",
      timePeriod: "WK",
      planBy: "ABS"
    },
    rows: {
      preset: null,
      categories: [],
      metrics: [
        "GIS1",
        "NIS3",
        "GIR",
        "END_OF_PERIOD_UNITS",
        "TARGET_STOCK_UNITS_TOTAL",
        "OPEN_TO_BUY_UNITS",
        "INTAKE_UNITS_TOTAL",
        "GROSS_WEEK_COVER",
        "NET_WEEK_COVER"
      ],
      yearOffsets: null,
      sortBy: "YoY"
    },
    layout: { gridStyleGrouping: "KPI Family" }
  }
};

/** The view a page opens on with no default of the user's own. */
export const DEFAULT_BUILT_IN_VIEW: BuiltInViewKey = "Compact YoY";

/**
 * The metric names a preset allows, or null for no restriction.
 *
 * ``allMetricNames`` comes from the live metric mapping, which is what makes
 * Per SQM a rule rather than a list.
 */
export function presetMetrics(
  preset: MetricPreset | null,
  allMetricNames: string[]
): string[] | null {
  switch (preset) {
    case "OP":
      return ONE_PAGER_METRICS;
    case "Compact":
      return COMPACT_METRICS;
    case "Per SQM":
      return [
        ...allMetricNames.filter(isPerSqmMetric),
        ...PER_SQM_EXTRA_METRICS.filter((extra) =>
          allMetricNames.includes(extra)
        )
      ];
    case "Full":
    case null:
    default:
      return null;
  }
}
